<template>
  <div>
    <span class="caption font-weight-medium" v-if="translatedLabel">
      {{ translatedLabel }} <span class="error--text" v-if="required">*</span>
    </span>
    <v-menu
      ref="menu"
      class="rounded-lg"
      v-model="menu"
      style="overflow: hidden"
      :close-on-content-click="false"
      transition="scale-transition"
      offset-y
      min-width="auto"
    >
      <template v-slot:activator="{ on, attrs }">
        <div v-if="showDay && singleDate" class="date-with-day-picker">
          <div
            class="date-picker-wrapper"
            :class="[
              color === 'white' ? 'custom-field-white' : 'custom-field',
              { 'date-picker-disabled': !allowEdit },
            ]"
            v-bind="attrs"
            v-on="on"
          >
            <v-btn
              icon
              :small="isMobile"
              @click.stop="changeDay(-1)"
              :disabled="!allowEdit || readonly"
              class="date-nav-btn left"
            >
              <v-icon :small="isMobile">mdi-chevron-left</v-icon>
            </v-btn>

            <div class="date-display">
              <div class="day-name">{{ getDayName(model) }}</div>
              <div class="date-value">{{ formattedDate(model) }}</div>
            </div>

            <v-btn
              icon
              :small="isMobile"
              @click.stop="changeDay(1)"
              :disabled="!allowEdit || readonly"
              class="date-nav-btn right"
            >
              <v-icon :small="isMobile">mdi-chevron-right</v-icon>
            </v-btn>

            <v-icon class="calendar-icon" v-if="!confirm && !isMobile"
              >mdi-calendar</v-icon
            >
            <v-btn
              v-else-if="confirm"
              icon
              @click.prevent="allowEdit = !allowEdit"
              class="lock-btn"
            >
              <v-icon>{{ allowEdit ? "mdi-lock-open" : "mdi-lock" }}</v-icon>
            </v-btn>
          </div>
        </div>
        <v-text-field
          v-else
          :value="
            singleDate
              ? formattedDate(model)
              : model.map((date) => localeDate(date)).join(' - ')
          "
          readonly
          :hide-details="hideDetails && !hint"
          :hint="hint"
          :persistent-hint="persistentHint"
          :rules="rules"
          :disabled="!allowEdit"
          dense
          class="input-text-center"
          :class="color === 'white' ? 'custom-field-white' : 'custom-field'"
          solo
          flat
          filled
          :readonly="readonly"
          v-bind="attrs"
          v-on="on"
        >
          <template v-slot:prepend-inner>
            <v-btn
              icon
              :small="isMobile"
              @click.stop="changeDay(-1)"
              :disabled="!allowEdit || readonly"
            >
              <v-icon :small="isMobile">mdi-chevron-left</v-icon>
            </v-btn>
          </template>

          <template v-slot:append>
            <v-btn
              icon
              :small="isMobile"
              @click.stop="changeDay(1)"
              :disabled="!allowEdit || readonly"
              class="mr-1"
            >
              <v-icon :small="isMobile">mdi-chevron-right</v-icon>
            </v-btn>
            <v-icon v-if="!confirm && !isMobile">mdi-calendar</v-icon>
            <v-btn v-else-if="confirm" icon @click.prevent="allowEdit = !allowEdit">
              <v-icon>{{ allowEdit ? "mdi-lock-open" : "mdi-lock" }}</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </template>

      <v-date-picker
        v-model="selectedDate"
        class="rounded-lg custom-shadow"
        :range="!singleDate"
        color="primary"
        :readonly="readonly"
        no-title
        :min="min"
        :max="max"
        :locale="locale"
        :allowed-dates="allowedDates"
        scrollable
      >
        <v-spacer></v-spacer>
        <v-btn text @click="menu = false">
          {{ $t("cancel") }}
        </v-btn>
        <v-btn color="success" class="custom-shadow rounded-lg" @click="saveSelection">
          {{ $t("save") }}
        </v-btn>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import hasModel from "@/mixins/hasModel";

export default {
  mixins: [hasModel],
  props: {
    label: {
      type: String,
      required: false,
      default: "",
    },
    disallowedDates: {
      type: Array,
      required: false,
      default: () => [],
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: true,
    },
    singleDate: {
      type: Boolean,
      required: false,
      default: false,
    },
    showDay: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    confirm: {
      type: Boolean,
      required: false,
      default: false,
    },
    color: {
      type: String,
      required: false,
      default: "default",
    },
    min: {
      type: [String, Boolean],
      required: false,
      default: undefined,
    },
    max: {
      type: [String, Boolean],
      required: false,
      default: undefined,
    },
    rules: {
      type: Array,
      required: false,
      default: () => [],
    },
    hint: {
      type: String,
      required: false,
      default: "",
    },
    persistentHint: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      menu: false,
      selectedDate: this.singleDate ? null : [],
      allowEdit: true,
    };
  },
  created() {
    if (this.disabled || this.confirm) this.allowEdit = false;

    // Initialize model for range picker if not set
    if (
      !this.singleDate &&
      (!this.model || !Array.isArray(this.model) || this.model.length === 0)
    ) {
      const today = new Date().toISOString().substr(0, 10);
      this.model = [today, today];
    }
  },
  watch: {
    disabled() {
      this.allowEdit = !(this.confirm || this.disabled);
    },
    menu(isOpen) {
      if (isOpen) {
        // When menu opens, copy the current committed model value to selectedDate
        if (this.singleDate) {
          this.selectedDate = this.model; // model is from hasModel, reflects prop 'value'
        } else {
          // For range, ensure it's an array and make a copy
          this.selectedDate = Array.isArray(this.model) ? [...this.model] : [];
        }
      } else {
        this.allowEdit = !(this.confirm || this.disabled);
      }
    },
    // Watch for external changes to the model (prop 'value')
    model: {
      handler(newValue) {
        // Only sync selectedDate from model if the menu is NOT open.
        // If menu is open, user might be in the middle of a selection.
        // When menu opens, the 'menu' watcher will correctly initialize selectedDate.
        if (!this.menu) {
          if (this.singleDate) {
            this.selectedDate = newValue;
          } else {
            this.selectedDate = Array.isArray(newValue) ? [...newValue] : [];
          }
        }
      },
      immediate: true, // Run handler immediately on component creation to set initial selectedDate
      deep: true, // Necessary for array model (date range)
    },
  },
  methods: {
    changeDay(days) {
      if (this.singleDate) {
        // If no model, use today's date as starting point
        const baseDate = this.model ? new Date(this.model) : new Date();
        baseDate.setDate(baseDate.getDate() + days);
        const newDate = baseDate.toISOString().substr(0, 10);

        // Check min/max constraints
        if (this.min && newDate < this.min) return;
        if (this.max && newDate > this.max) return;

        this.model = newDate;
      } else {
        // Handle range date picker
        if (Array.isArray(this.model) && this.model.length >= 1) {
          if (this.model.length === 2) {
            // If we have both dates, move both by the same amount
            const startDate = new Date(this.model[0]);
            const endDate = new Date(this.model[1]);

            startDate.setDate(startDate.getDate() + days);
            endDate.setDate(endDate.getDate() + days);

            const newStart = startDate.toISOString().substr(0, 10);
            const newEnd = endDate.toISOString().substr(0, 10);

            // Check min/max constraints
            if (this.min && newStart < this.min) return;
            if (this.max && newEnd > this.max) return;

            this.model = [newStart, newEnd];
          } else if (this.model.length === 1) {
            // If only one date, move it
            const currentDate = new Date(this.model[0]);
            currentDate.setDate(currentDate.getDate() + days);
            const newDate = currentDate.toISOString().substr(0, 10);

            if (this.min && newDate < this.min) return;
            if (this.max && newDate > this.max) return;

            this.model = [newDate, newDate];
          }
        } else {
          // If no model or empty array for range picker, use today as starting point
          const today = new Date();
          today.setDate(today.getDate() + days);
          const newDate = today.toISOString().substr(0, 10);

          if (this.min && newDate < this.min) return;
          if (this.max && newDate > this.max) return;

          this.model = [newDate, newDate];
        }
      }
    },
    localeDate(date) {
      if (!date) return "";
      const dateObj = new Date(date + "T00:00:00"); // Avoid timezone issues
      return dateObj.toLocaleDateString(this.locale || "sr-RS", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    },
    formattedDate(date) {
      if (!date) return "";
      return this.localeDate(date);
    },
    getDayName(date) {
      if (!date) return "";
      const dateObj = new Date(date + "T00:00:00");
      return dateObj.toLocaleDateString(this.locale || "sr-RS", {
        weekday: "long",
      });
    },
    allowedDates(val) {
      return !this.disallowedDates.includes(val);
    },

    saveSelection() {
      if (!this.singleDate && Array.isArray(this.selectedDate)) {
        if (this.selectedDate.length === 2) {
          // Ensure dates are sorted (YYYY-MM-DD format allows string sort)
          const sorted = [...this.selectedDate].sort();
          this.model = sorted; // Assign a new array for reactivity
        } else if (this.selectedDate.length === 1) {
          // If only one date is selected, set model to [date, date]
          const date = this.selectedDate[0];
          this.model = [date, date];
        }
      } else if (this.singleDate && this.selectedDate) {
        const newDate =
          typeof this.selectedDate === "string"
            ? "" + this.selectedDate // Force new string instance
            : this.selectedDate;
        this.model = newDate;
      }
      this.menu = false; // Close the menu
    },
  },
  computed: {
    locale() {
      let locale = "";
      switch (this.$i18n.locale) {
        case "en":
          locale = "en-GB";
          break;
        case "hr":
          locale = "sr-RS";
          break;
        case "mk":
          locale = "mk-MK";
          break;
        case "si":
          locale = "sl-SI";
          break;
        case "ba":
          locale = "bs-BA";
          break;
        case "sr":
        case "sr-lat":
          locale = "sr-RS";
          break;
        case "rs":
        case "rs-lat":
          locale = "sr-RS";
          break;
      }
      return locale;
    },
    translatedLabel() {
      if (!this.label) return this.$t("date");
      return this.label;
    },
    isMobile() {
      return this.device?.isMobile || false;
    },
  },
};
</script>

<style>
.input-text-center input {
  text-align: center;
}

.date-picker-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 4px;
  border-radius: 4px;
  min-height: 40px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.date-picker-wrapper.custom-field {
  background-color: #f5f5f5;
}

.date-picker-wrapper.custom-field-white {
  background-color: white;
}

.date-picker-wrapper.date-picker-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.date-picker-wrapper:hover:not(.date-picker-disabled) {
  background-color: #eeeeee;
}

.date-picker-wrapper.custom-field-white:hover:not(.date-picker-disabled) {
  background-color: #fafafa;
}

.date-nav-btn {
  flex-shrink: 0;
}

.date-display {
  flex: 1;
  text-align: center;
  padding: 4px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.day-name {
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
  color: rgba(0, 0, 0, 0.6);
  line-height: 1;
}

.date-value {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1;
}

.calendar-icon,
.lock-btn {
  flex-shrink: 0;
  margin-left: 4px;
}

.date-with-day-picker {
  border: 1px solid rgba(0, 0, 0, 0.38) !important;
  border-radius: 8px;
  overflow: hidden;
}
</style>
